import React, { useEffect, useMemo, useRef, useState } from "react";
import { URL } from "../../utils/constants";
import "./FiltersPanel.css";

const statusRu = {
  created: "Создана",
  in_work: "В работе",
  ready: "Готово",
  done: "Выполнена",
};

function FilterList({
  title,
  hint,
  items,
  selectedSet,
  searchValue,
  onSearch,
  onToggle,
  onClear,
  maxHeight = 220,
  extraHeaderRight,
  renderRow,
}) {
  const selectedCount = selectedSet?.size || 0;

  return (
    <div className="filters-section">
      <div className="filters-section__head">
        <div className="filters-section__title">
          {title} <span className="filters-section__count">({selectedCount})</span>
        </div>

        <div className="filters-section__right">
          {extraHeaderRight}
          <button
            type="button"
            className="filters-clearBtn"
            onClick={onClear}
            disabled={!selectedCount}
          >
            Очистить
          </button>
        </div>
      </div>

      {hint ? <div className="filters-hint">{hint}</div> : null}

      <div className="filters-searchBlock">
        <input
          className="filters-input"
          placeholder="Поиск…"
          value={searchValue}
          onChange={(e) => onSearch(e.target.value)}
        />

        <div className="filters-scroll" style={{ maxHeight }}>
          {!items?.length ? (
            <div className="filters-hint">Нет данных</div>
          ) : (
            <div className="filters-checkList">
              {items.map((it) => {
                const checked = selectedSet?.has(Number(it.id));
                return (
                  <label key={it.id} className="filters-checkRow">
                    <input
                      type="checkbox"
                      checked={!!checked}
                      onChange={() => onToggle(it.id)}
                    />
                    {renderRow ? renderRow(it) : <span className="filters-checkText">{it.label}</span>}
                  </label>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function FiltersPanel({
  mapRef,
  ymapsApiRef,
  embedded = false,
  forceCollapsed,
  onCollapsedChange,
  tokenProp,
  onOckMatchChange,
}) {
  const token = tokenProp || localStorage.getItem("auth_token");

  const [collapsedLocal, setCollapsedLocal] = useState(() => {
    try {
      return localStorage.getItem("filters_panel_collapsed") === "1";
    } catch {
      return false;
    }
  });

  const collapsed =
    typeof forceCollapsed === "boolean" ? forceCollapsed : collapsedLocal;

  const setCollapsed = (vOrFn) => {
    const next = typeof vOrFn === "function" ? vOrFn(collapsed) : vOrFn;
    if (typeof forceCollapsed === "boolean") onCollapsedChange?.(next);
    else setCollapsedLocal(next);
  };

  useEffect(() => {
    if (typeof forceCollapsed === "boolean") return;
    try {
      localStorage.setItem("filters_panel_collapsed", collapsed ? "1" : "0");
    } catch {}
  }, [collapsed, forceCollapsed]);

  // данные
  const [users, setUsers] = useState([]);
  const [points, setPoints] = useState([]);
  const [objects, setObjects] = useState([]);
  const [stages, setStages] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [loadingPoints, setLoadingPoints] = useState(false);
  const [loadingObjects, setLoadingObjects] = useState(false);
  const [loadingStages, setLoadingStages] = useState(false);
  const [errorText, setErrorText] = useState("");

  const [resultsSearch, setResultsSearch] = useState("");

  const prevSigRef = useRef("");

  // поиск
  const setSearchValue = (key, value) => {
    setFilters((prev) => ({
      ...prev,
      search: { ...prev.search, [key]: value },
    }));
  };

  // ФИЛЬТРАЦИЯ
  const [filters, setFilters] = useState({
    selected: {
      execIds: new Set(),      // исполнители
      objectIds: new Set(),    // объекты
      stageIds: new Set(),     // этапы
      pointIds: new Set(),     // точки (ручная выборка)
    },
    search: {
      exec: "",
      objects: "",
      stages: "",
      points: "",
    },
    pointSearchMode: "id", // "id" | "address" | "inv"
    logic: {
      withinGroup: "ANY",  // ANY/ALL внутри группы
      betweenGroups: "AND" // AND/OR между группами
    }
  });

  const toggleSelected = (key, id) => {
    const n = Number(id);
    if (!Number.isFinite(n)) return;       
    setFilters((prev) => {
      const prevSet = prev.selected?.[key] || new Set();
      const nextSet = new Set(prevSet);
      if (nextSet.has(n)) nextSet.delete(n);
      else nextSet.add(n);  
      return {
      ...prev,
      selected: { ...prev.selected, [key]: nextSet },
      };
    });
  };

  const clearSelected = (key) => {
    setFilters((prev) => ({
        ...prev,
        selected: { ...prev.selected, [key]: new Set() },
    }));
  };

  const resetAllFilters = () => {
    setFilters({
      selected: {
        execIds: new Set(),
        objectIds: new Set(),
        stageIds: new Set(),
        pointIds: new Set(),
      },
      search: { exec: "", objects: "", stages: "", points: "" },
      pointSearchMode: "id",
      logic: { withinGroup: "ANY", betweenGroups: "AND" },
    });
  };

  const normalizeArray = (data) => {
    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.results)) return data.results;
    return [];
  };

  const formatUserShortName = (u) => {
    const lastName = String(u?.last_name || "").trim();
    const firstName = String(u?.first_name || "").trim();
    const middleName = String(u?.middle_name || u?.patronymic || "").trim();

    const firstInitial = firstName ? `${firstName[0]}.` : "";
    const middleInitial = middleName ? `${middleName[0]}.` : "";

    if (lastName && (firstInitial || middleInitial)) {
      return `${lastName} ${firstInitial}${middleInitial}`.trim();
    }
    if (lastName) return lastName;
    if (firstName) return firstName;
    return String(u?.username || u?.email || `ID ${u?.id ?? ""}`).trim();
  };

  const pointsIndex = useMemo(() => {
    return (points || []).map(p => ({
      p,
      id: Number(p.id),
      execIds: (p.executors || []).map(Number),
      objectIds: [
      ...(p.object_ref ? [Number(p.object_ref)] : []),
      ...((p.objects_ock || []).map(Number)),
      ],
      stageIds: [
      ...(p.main_stage ? [Number(p.main_stage)] : []),
      ...((p.stages || []).map(Number)),
      ],
      address: String(p.address || ""),
      inv: String(p.inventory_number || ""),
    }));
  }, [points]);

  const hasAny = (arr, set) => arr.some(x => set.has(x));
  const hasAll = (arr, set) => {
    for (const x of set) if (!arr.includes(x)) return false;
    return true;
  };

  const matchSet = useMemo(() => {
    const execSet = filters.selected.execIds;
    const objSet  = filters.selected.objectIds;
    const stSet   = filters.selected.stageIds;
    const ptSet   = filters.selected.pointIds;

    const hasWithin = filters.logic.withinGroup === "ALL" ? hasAll : hasAny;

    const result = new Set();

    for (const item of pointsIndex) {
      const passExec = execSet.size ? hasWithin(item.execIds, execSet) : true;
      const passObj  = objSet.size  ? hasWithin(item.objectIds, objSet) : true;
      const passSt   = stSet.size   ? hasWithin(item.stageIds, stSet) : true;
      const passPt   = ptSet.size   ? ptSet.has(item.id) : true;  
      // betweenGroups AND
      const ok = passExec && passObj && passSt && passPt;
      if (ok) result.add(item.id);
    }

    // если ни одна группа не активна — фильтра нет -> null (для карты)
    const anyActive =
      execSet.size || objSet.size || stSet.size || ptSet.size ||
      filters.search.exec.trim() || filters.search.objects.trim() ||
      filters.search.stages.trim() || filters.search.points.trim();

    return anyActive ? result : null;
  }, [filters, pointsIndex]);

  const pointsTotal = points.length;
  const matchedCount = matchSet ? matchSet.size : pointsTotal;

  const execItemsFiltered = useMemo(() => {
    const q = filters.search.exec.trim().toLowerCase();
    return (users || [])
      .map(u => ({ id: u.id, label: formatUserShortName(u) }))
      .filter(it => !q || it.label.toLowerCase().includes(q));
  }, [users, filters.search.exec]);

  const objectItemsFiltered = useMemo(() => {
    const q = filters.search.objects.trim().toLowerCase();
    return (objects || [])
      .map(o => ({ id: o.id, label: o.name }))
      .filter(it => !q || it.label.toLowerCase().includes(q));
  }, [objects, filters.search.objects]);

  const stageItemsFiltered = useMemo(() => {
    const q = filters.search.stages.trim().toLowerCase();
    return (stages || [])
        .map(s => ({
        id: s.id,
        label: `${s.stage_type_detail?.name || "—"} — ${s.name}`,
      }))
      .filter(it => !q || it.label.toLowerCase().includes(q));
  }, [stages, filters.search.stages]);

  const pointItemsFiltered = useMemo(() => {
    const mode = filters.pointSearchMode;
    const q = filters.search.points.trim().toLowerCase();

    // кандидаты можно сделать уже “после первых фильтров”, но UI-часть такая:
    return (points || [])
      .map(p => {
        const inv = p.inventory_number || "";
        const addr = p.address || "";
        const label = `#${p.id}`;
        const sub = mode === "address" ? addr : mode === "inv" ? inv : addr; // можно показать адрес всегда  
        // строка поиска по выбранному полю
        const field =
          mode === "id" ? String(p.id) :
          mode === "inv" ? String(inv) :
          String(addr);  
        return { id: p.id, label, sub, __field: field };
      })
      .filter(it => !q || String(it.__field).toLowerCase().includes(q))
      .slice(0, 500); // ограничение чтобы не рендерить 10k чекбоксов
  }, [points, filters.search.points, filters.pointSearchMode]);

  const resultsFiltered = useMemo(() => {
    // база: либо все точки (если фильтр не активен), либо только подходящие
    const base = matchSet
      ? points.filter((p) => matchSet.has(Number(p.id)))
      : points;

    const q = resultsSearch.trim().toLowerCase();
    if (!q) return base;

    return base.filter((p) => {
      const hay = `${p.id ?? ""} ${p.address ?? ""} ${p.inventory_number ?? ""}`.toLowerCase();
      return hay.includes(q);
    });
  }, [points, matchSet, resultsSearch]);

  const hasActiveFilters = useMemo(() => {
    const s = filters.selected;
    const q = filters.search;

    return (
      (s.execIds?.size || 0) > 0 ||
      (s.objectIds?.size || 0) > 0 ||
      (s.stageIds?.size || 0) > 0 ||
      (s.pointIds?.size || 0) > 0 ||
      !!q.exec.trim() ||
      !!q.objects.trim() ||
      !!q.stages.trim() ||
      !!q.points.trim()
    );
  }, [filters]);

  // ЗАГРУЗКА ДАННЫХ
  // загрузка users
  useEffect(() => {
    if (!token) return;
    let cancelled = false;

    (async () => {
      setLoadingUsers(true);
      setErrorText("");
      try {
        const r = await fetch(`${URL}/api/users/`, {
          headers: { Authorization: `Token ${token}` },
        });
        if (!r.ok) throw new Error(`HTTP ${r.status} /api/users/`);
        const data = await r.json();
        if (cancelled) return;
        setUsers(normalizeArray(data));
      } catch (e) {
        if (!cancelled) setErrorText(String(e?.message || e));
      } finally {
        if (!cancelled) setLoadingUsers(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [token]);

  // загрузка points-ock
  useEffect(() => {
    if (!token) return;
    let cancelled = false;

    (async () => {
      setLoadingPoints(true);
      setErrorText("");
      try {
        const r = await fetch(`${URL}/api/points-ock/`, {
          headers: { Authorization: `Token ${token}` },
        });
        if (!r.ok) throw new Error(`HTTP ${r.status} /api/points-ock/`);
        const data = await r.json();
        if (cancelled) return;
        setPoints(normalizeArray(data));
      } catch (e) {
        if (!cancelled) setErrorText(String(e?.message || e));
      } finally {
        if (!cancelled) setLoadingPoints(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [token]);

  // загрузка объектов
  useEffect(() => {
    if (!token) return;
    let cancelled = false;

    (async () => {
      setLoadingObjects(true);
      setErrorText("");
      try {
        const r = await fetch(`${URL}/api/ock-objects/`, {
          headers: { Authorization: `Token ${token}` },
        });
        if (!r.ok) throw new Error(`HTTP ${r.status} /api/ock-objects/`);
        const data = await r.json();
        if (cancelled) return;
        setObjects(normalizeArray(data));
      } catch (e) {
        if (!cancelled) setErrorText(String(e?.message || e));
      } finally {
        if (!cancelled) setLoadingObjects(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [token]);

  // загрузка этапов
  useEffect(() => {
    if (!token) return;
    let cancelled = false;

    (async () => {
      setLoadingStages(true);
      setErrorText("");
      try {
        const r = await fetch(`${URL}/api/points-ock-stages/`, {
          headers: { Authorization: `Token ${token}` },
        });
        if (!r.ok) throw new Error(`HTTP ${r.status} /api/points-ock-stages/`);
        const data = await r.json();
        if (cancelled) return;
        setStages(normalizeArray(data));
      } catch (e) {
        if (!cancelled) setErrorText(String(e?.message || e));
      } finally {
        if (!cancelled) setLoadingStages(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [token]);

  useEffect(() => {
    if (!onOckMatchChange) return;

    // matchSet: Set<number> | null
    if (!matchSet) {
       if (prevSigRef.current !== "null") {
        prevSigRef.current = "null";
        onOckMatchChange(null);
       }
       return;
    }

    // превращаем в отсортированный массив, чтобы сигнатура была стабильной
    const ids = Array.from(matchSet).map(Number).filter(Number.isFinite).sort((a,b) => a-b);
    const sig = ids.join(",");

    if (sig !== prevSigRef.current) {
      prevSigRef.current = sig;
      onOckMatchChange(ids);
    }
  }, [matchSet, onOckMatchChange]);

  if (!token) return null;

  return (
    <>
      {!embedded && (
        <div className="filters-toggle-wrap">
          <button
            className="filters-toggle-btn"
            aria-expanded={!collapsed}
            title={collapsed ? "Развернуть фильтры" : "Свернуть фильтры"}
            onClick={() => setCollapsed((v) => !v)}
          >
            {collapsed ? "Фильтры ▸" : "Фильтры ▾"}
          </button>
        </div>
      )}

      {!collapsed && (
        <div className={`filters-panel ${embedded ? "filters-panel--embedded" : ""}`}>
          <div className="filters-panel__title">Фильтры ОСК</div>
          <div className="filters-twoBlocks">
            {/* ===== БЛОК 1: ФИЛЬТРЫ ===== */}
            <div className="filters-card">
              <div className="filters-card__title">Фильтры</div>  
              <FilterList
                title="Исполнители"
                items={execItemsFiltered}                 // [{id,label}]
                selectedSet={filters.selected.execIds}
                searchValue={filters.search.exec}
                onSearch={(v) => setSearchValue("exec", v)}
                onToggle={(id) => toggleSelected("execIds", id)}
                onClear={() => clearSelected("execIds")}
                maxHeight={200}
              />  
              <FilterList
                title="Объекты"
                items={objectItemsFiltered}
                selectedSet={filters.selected.objectIds}
                searchValue={filters.search.objects}
                onSearch={(v) => setSearchValue("objects", v)}
                onToggle={(id) => toggleSelected("objectIds", id)}
                onClear={() => clearSelected("objectIds")}
                maxHeight={200}
              />  
              <FilterList
                title="Этапы"
                items={stageItemsFiltered}                 // label: "Тип — Этап"
                selectedSet={filters.selected.stageIds}
                searchValue={filters.search.stages}
                onSearch={(v) => setSearchValue("stages", v)}
                onToggle={(id) => toggleSelected("stageIds", id)}
                onClear={() => clearSelected("stageIds")}
                maxHeight={200}
              />  
              <FilterList
                title="Точки"
                hint="Выбор точки сужает результаты до отмеченных точек (если выбрано хотя бы одна)."
                items={pointItemsFiltered}                 // [{id,label,extra?}]
                selectedSet={filters.selected.pointIds}
                searchValue={filters.search.points}
                onSearch={(v) => setSearchValue("points", v)}
                onToggle={(id) => toggleSelected("pointIds", id)}
                onClear={() => clearSelected("pointIds")}
                maxHeight={240}
                extraHeaderRight={
                    <select
                      className="filters-selectMini"
                      value={filters.pointSearchMode}
                      onChange={(e) =>
                        setFilters((prev) => ({ ...prev, pointSearchMode: e.target.value }))
                      }
                      title="Режим поиска точек"
                    >
                    <option value="id">№ точки</option>
                    <option value="address">Адрес</option>
                    <option value="inv">Инв. №</option>
                    </select>
                }
                renderRow={(it) => (
                    <span className="filters-checkText">
                    {it.label}
                    {it.sub ? <span className="filters-subText"> — {it.sub}</span> : null}
                    </span>
                )}
              />  
              <div className="filters-actions">
                <button
                  type="button"
                  className="filters-btn filters-btn--clearAll"
                  onClick={resetAllFilters}
                >
                  Сбросить всё
                </button>
              </div>
            </div>  
            {/* ===== БЛОК 2: РЕЗУЛЬТАТЫ ===== */}
            {hasActiveFilters && (
              <div className="filters-card">
                <div className="filters-card__title">Результаты</div>  
                <div className="filters-summaryRow">
                  Подходящих точек: <b>{matchedCount}</b> из <b>{pointsTotal}</b>
                </div>  
                  <div className="filters-searchBlock">
                    <input
                      className="filters-input"
                      placeholder="Поиск в результатах (ID/адрес/инв)…"
                      value={resultsSearch}
                      onChange={(e) => setResultsSearch(e.target.value)}
                    />
                  <div className="filters-scroll" style={{ maxHeight: 320 }}>
                    {/* список результатов */}
                    <div className="filters-list">
                      {resultsFiltered.map((p) => (
                        <div key={p.id} className="filters-list__row">
                        <span className="filters-pill">#{p.id}</span>
                        <span className="filters-list__text">{p.address || "—"}</span>
                        <span className="filters-mini">{statusRu[p.status] || p.status}</span>
                        <span className={`filters-vis ${p.is_visible === false ? "filters-vis--off" : ""}`}>
                            {p.is_visible === false ? "скрыта" : "видна"}
                        </span>
                        </div>
                      ))}
                      {!resultsFiltered.length ? <div className="filters-hint">Совпадений нет</div> : null}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}